import { Injectable, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { DEFAULT_ROWS_PER_PAGE, PAGE_SIZE_OPTIONS } from 'src/app/common/constants-ejb';
import {
  InformacionTarea,
  InformacionTareaFiltro
} from 'src/app/domain/model/orden-servicio/orden-servicio';
import { OficinaRepository } from 'src/app/domain/service/oficina/oficina-repository';
import { OrdenServicioRepository } from 'src/app/domain/service/orden-servicio/orden-servicio-repository';
import { UsuarioSesion } from 'src/app/domain/model/usuario/usuario-sesion';
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';
import type {
  AsesorSeleccionado
} from '../../common/busquedas/busqueda-asesor-dialog.component';
import type {
  ClienteSeleccionado
} from '../../common/busquedas/busqueda-cliente-dialog.component';
import {
  IT_DETALLE_NAVIGATION_STATE_ID,
  IT_DETALLE_NAVIGATION_STATE_QUERY_PARAMS_REGRESO
} from './informacion-tareas-detalle.constants';
import type { OficinaOption } from './informacion-tareas.types';

const DIAS_ATRAS_DEFAULT = 30;
const PARAM_FILTRO_ORDEN = 'filtroOrden';
const PARAM_FECHA_DESDE = 'fechaEntregaDesde';
const PARAM_FECHA_HASTA = 'fechaEntregaHasta';
const PARAM_OFICINA = 'nmconsecutivoOficina';
const PARAM_ASESOR_ID = 'nmconsecutivoAsesor';
const PARAM_ASESOR_NOMBRE = 'nombreAsesor';
const PARAM_BENEFICIARIO_ID = 'nmconsecutivoBeneficiario';
const PARAM_BENEFICIARIO_NOMBRE = 'nombreBeneficiario';
const PARAM_AUTO_BUSCAR = 'autobuscar';

/**
 * Facade de estado y lógica de negocio para HU1 - Informacion de Tareas.
 *
 * Legacy: InformacionTareasViewImpl / InformacionTareasActivity
 */
@Injectable()
export class InformacionTareasFacade {
  private readonly ordenServicioRepository = inject(OrdenServicioRepository);
  private readonly oficinaRepository = inject(OficinaRepository);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly usuarioState = inject(UsuarioStateService);

  readonly loading = signal(false);
  readonly consultando = signal(false);
  readonly mostrarResultados = signal(false);
  readonly items = signal<InformacionTarea[]>([]);
  readonly oficinas = signal<OficinaOption[]>([]);

  readonly currentPage = signal(1);
  readonly pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  readonly totalPages = signal(0);
  readonly totalRegistros = signal(0);
  readonly hasNext = signal(false);
  readonly hasPrevious = signal(false);

  readonly registroInicio = computed(() => {
    if (this.totalRegistros() === 0) {
      return 0;
    }
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });

  readonly registroFin = computed(() => {
    if (this.totalRegistros() === 0) {
      return 0;
    }
    const fin = this.registroInicio() + this.items().length - 1;
    return Math.min(fin, this.totalRegistros());
  });

  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly selectedPageSize = signal(DEFAULT_ROWS_PER_PAGE);

  readonly filtroOrden = signal('');
  readonly fechaEntregaDesde = signal<Date | null>(null);
  readonly fechaEntregaHasta = signal<Date | null>(null);
  readonly filtroOficina = signal<number | null>(null);

  readonly asesorSeleccionado = signal<AsesorSeleccionado | null>(null);
  readonly beneficiarioSeleccionado = signal<ClienteSeleccionado | null>(null);

  readonly nombreAsesor = signal('');
  readonly nombreBeneficiario = signal('');

  readonly dialogBuscarAsesorVisible = signal(false);
  readonly dialogBuscarBeneficiarioVisible = signal(false);

  private usuarioSesion: UsuarioSesion | null = null;
  private diasAtrasVisualizacion = DIAS_ATRAS_DEFAULT;

  async init(): Promise<void> {
    this.loading.set(true);
    try {
      this.usuarioSesion = await this.usuarioState.cargar();
      await Promise.all([this.cargarDiasAtrasVisualizacion(), this.cargarOficinas()]);

      this.aplicarFechasDefault();
      this.aplicarAsesorSesionSiCorresponde();

      const debeBuscar = this.rehidratarFiltrosDesdeNavegacion();
      if (debeBuscar) {
        await this.buscar(0);
      }
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No fue posible cargar la configuracion inicial de Informacion de Tareas'
      });
    } finally {
      this.loading.set(false);
    }
  }

  async buscar(pageIndex: number = 0): Promise<void> {
    if (!this.validarFechas()) {
      return;
    }

    const filtro = this.construirFiltro(pageIndex);

    this.consultando.set(true);
    try {
      const response = await this.ordenServicioRepository.consultarInformacionTareas(filtro);

      this.items.set(response.content ?? []);
      this.totalRegistros.set(response.totalElements ?? 0);
      this.totalPages.set(response.totalPages ?? 0);
      this.currentPage.set((response.pageNumber ?? pageIndex) + 1);
      this.hasNext.set(response.hasNext ?? false);
      this.hasPrevious.set(response.hasPrevious ?? false);

      if (!response.content || response.content.length === 0) {
        this.mostrarResultados.set(false);
        this.messageService.add({
          severity: 'warn',
          summary: 'Sin resultados',
          detail: 'La busqueda no arrojo resultados'
        });
        return;
      }

      this.mostrarResultados.set(true);
    } catch {
      this.mostrarResultados.set(false);
      this.items.set([]);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No fue posible consultar la informacion de tareas'
      });
    } finally {
      this.consultando.set(false);
    }
  }

  limpiarFiltros(): void {
    this.filtroOrden.set('');
    this.limpiarBeneficiario();

    if (this.usuarioState.esAsesor()) {
      this.aplicarAsesorSesionSiCorresponde();
    } else {
      this.limpiarAsesor();
    }

    // Legacy: lbOficina.setSelectedIndex(0) - vuelve a la primera opcion del combo,
    // NO a la oficina de sesion.
    this.filtroOficina.set(this.oficinas().length > 0 ? this.oficinas()[0].value : null);
    this.aplicarFechasDefault();

    this.items.set([]);
    this.mostrarResultados.set(false);
    this.totalRegistros.set(0);
    this.totalPages.set(0);
    this.currentPage.set(1);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
  }

  abrirBuscadorAsesor(): void {
    this.dialogBuscarAsesorVisible.set(true);
  }

  cerrarBuscadorAsesor(): void {
    this.dialogBuscarAsesorVisible.set(false);
  }

  abrirBuscadorBeneficiario(): void {
    this.dialogBuscarBeneficiarioVisible.set(true);
  }

  cerrarBuscadorBeneficiario(): void {
    this.dialogBuscarBeneficiarioVisible.set(false);
  }

  onAsesorSeleccionado(asesor: AsesorSeleccionado): void {
    this.asesorSeleccionado.set(asesor);
    this.nombreAsesor.set(this.formatearNombreAsesor(asesor));
    this.dialogBuscarAsesorVisible.set(false);
  }

  onBeneficiarioSeleccionado(beneficiario: ClienteSeleccionado): void {
    this.beneficiarioSeleccionado.set(beneficiario);
    this.nombreBeneficiario.set(beneficiario.nombreComercial || '');
    this.dialogBuscarBeneficiarioVisible.set(false);
  }

  limpiarAsesor(): void {
    this.asesorSeleccionado.set(null);
    this.nombreAsesor.set('');
  }

  limpiarBeneficiario(): void {
    this.beneficiarioSeleccionado.set(null);
    this.nombreBeneficiario.set('');
  }

  onPageSizeChange(size: number): void {
    this.selectedPageSize.set(size);
    this.pageSize.set(size);
    if (this.mostrarResultados()) {
      this.buscar(0);
    }
  }

  irPrimeraPagina(): void {
    if (this.currentPage() <= 1) {
      return;
    }
    this.buscar(0);
  }

  irPaginaAnterior(): void {
    if (!this.hasPrevious()) {
      return;
    }
    this.buscar(this.currentPage() - 2);
  }

  irPaginaSiguiente(): void {
    if (!this.hasNext()) {
      return;
    }
    this.buscar(this.currentPage());
  }

  irUltimaPagina(): void {
    if (this.totalPages() <= 0 || this.currentPage() >= this.totalPages()) {
      return;
    }
    this.buscar(this.totalPages() - 1);
  }

  seleccionarOrden(item: InformacionTarea): void {
    this.router.navigate(['/informacion-tareas/detalle'], {
      state: {
        [IT_DETALLE_NAVIGATION_STATE_ID]: item.nmconsecutivo,
        [IT_DETALLE_NAVIGATION_STATE_QUERY_PARAMS_REGRESO]:
          this.construirQueryParamsNavegacion()
      }
    });
  }

  private async cargarOficinas(): Promise<void> {
    const oficinas = await this.oficinaRepository.obtenerOficinas();
    const opciones: OficinaOption[] = [
      { label: 'TODAS', value: null },
      ...oficinas.map((oficina) => ({
        label: oficina.dsoficina,
        value: oficina.nmconsecutivo
      }))
    ];

    this.oficinas.set(opciones);

    const oficinaSesion = this.usuarioSesion?.nmconsecOficina;
    if (oficinaSesion && opciones.some((item) => item.value === oficinaSesion)) {
      this.filtroOficina.set(oficinaSesion);
      return;
    }

    this.filtroOficina.set(opciones[0]?.value ?? null);
  }

  private async cargarDiasAtrasVisualizacion(): Promise<void> {
    const configuracion = await this.ordenServicioRepository.obtenerConfiguracionInformacionTareas();
    const parsed = Number(configuracion?.diasAtrasVisualizacion);
    this.diasAtrasVisualizacion =
      Number.isFinite(parsed) && parsed >= 0 ? parsed : DIAS_ATRAS_DEFAULT;
  }

  private aplicarFechasDefault(): void {
    const hoy = new Date();
    const fin = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    const inicio = new Date(fin);
    inicio.setDate(inicio.getDate() - this.diasAtrasVisualizacion);

    this.fechaEntregaDesde.set(inicio);
    this.fechaEntregaHasta.set(fin);
  }

  private aplicarAsesorSesionSiCorresponde(): void {
    if (!this.usuarioState.esAsesor() || !this.usuarioSesion) {
      return;
    }

    const asesor: AsesorSeleccionado = {
      nmconsecutivo: this.usuarioSesion.nmconsecutivo,
      cdpersona: this.usuarioSesion.cdpersona,
      cdtipoIdentificacion: this.usuarioSesion.tipoIdentificacion,
      nmidentificacion: this.usuarioSesion.numeroIdentificacion,
      dsnombre1: this.usuarioSesion.primerNombre ?? '',
      dsnombre2: this.usuarioSesion.segundoNombre ?? '',
      dsapellido1: this.usuarioSesion.primerApellido ?? '',
      dsapellido2: this.usuarioSesion.segundoApellido ?? '',
      dsnombreComercial: this.usuarioSesion.nombreCompleto ?? '',
      snactivo: 'S'
    };

    this.asesorSeleccionado.set(asesor);
    this.nombreAsesor.set(this.formatearNombreAsesor(asesor));
  }

  private construirFiltro(pageIndex: number): InformacionTareaFiltro {
    return {
      orden: this.filtroOrden()?.trim() || undefined,
      fechaEntregaDesde: this.toIsoDate(this.fechaEntregaDesde()),
      fechaEntregaHasta: this.toIsoDate(this.fechaEntregaHasta()),
      nmconsecutivoBeneficiario: this.beneficiarioSeleccionado()?.nmconsecutivo,
      nmconsecutivoAsesor: this.asesorSeleccionado()?.nmconsecutivo,
      nmconsecutivoOficina: this.filtroOficina() ?? undefined,
      page: pageIndex,
      size: this.pageSize()
    };
  }

  private construirQueryParamsNavegacion(): Record<string, string | number> {
    const params: Record<string, string | number> = {
      [PARAM_FECHA_DESDE]: this.toIsoDate(this.fechaEntregaDesde()),
      [PARAM_FECHA_HASTA]: this.toIsoDate(this.fechaEntregaHasta()),
      [PARAM_AUTO_BUSCAR]: '1'
    };

    const orden = this.filtroOrden()?.trim();
    if (orden) {
      params[PARAM_FILTRO_ORDEN] = orden;
    }

    if (this.filtroOficina() !== null && this.filtroOficina() !== undefined) {
      params[PARAM_OFICINA] = this.filtroOficina() as number;
    }

    const asesor = this.asesorSeleccionado();
    if (asesor?.nmconsecutivo) {
      params[PARAM_ASESOR_ID] = asesor.nmconsecutivo;
    }
    if (this.nombreAsesor().trim()) {
      params[PARAM_ASESOR_NOMBRE] = this.nombreAsesor().trim();
    }

    const beneficiario = this.beneficiarioSeleccionado();
    if (beneficiario?.nmconsecutivo) {
      params[PARAM_BENEFICIARIO_ID] = beneficiario.nmconsecutivo;
    }
    if (this.nombreBeneficiario().trim()) {
      params[PARAM_BENEFICIARIO_NOMBRE] = this.nombreBeneficiario().trim();
    }

    return params;
  }

  private rehidratarFiltrosDesdeNavegacion(): boolean {
    const queryParamMap = this.route.snapshot.queryParamMap;

    const filtroOrden = queryParamMap.get(PARAM_FILTRO_ORDEN);
    if (filtroOrden) {
      this.filtroOrden.set(filtroOrden);
    }

    const fechaDesde = this.parseIsoDate(queryParamMap.get(PARAM_FECHA_DESDE));
    if (fechaDesde) {
      this.fechaEntregaDesde.set(fechaDesde);
    }

    const fechaHasta = this.parseIsoDate(queryParamMap.get(PARAM_FECHA_HASTA));
    if (fechaHasta) {
      this.fechaEntregaHasta.set(fechaHasta);
    }

    const oficina = this.toNumber(queryParamMap.get(PARAM_OFICINA));
    if (oficina !== null) {
      this.filtroOficina.set(oficina);
    }

    const asesorId = this.toNumber(queryParamMap.get(PARAM_ASESOR_ID));
    const asesorNombre = queryParamMap.get(PARAM_ASESOR_NOMBRE) ?? '';
    if (asesorId !== null) {
      this.asesorSeleccionado.set(this.construirAsesorParcial(asesorId, asesorNombre));
      this.nombreAsesor.set(asesorNombre);
    }

    const beneficiarioId = this.toNumber(queryParamMap.get(PARAM_BENEFICIARIO_ID));
    const beneficiarioNombre = queryParamMap.get(PARAM_BENEFICIARIO_NOMBRE) ?? '';
    if (beneficiarioId !== null) {
      this.beneficiarioSeleccionado.set({
        nmconsecutivo: beneficiarioId,
        nombreComercial: beneficiarioNombre
      } as ClienteSeleccionado);
      this.nombreBeneficiario.set(beneficiarioNombre);
    }

    return queryParamMap.get(PARAM_AUTO_BUSCAR) === '1';
  }

  private construirAsesorParcial(
    nmconsecutivoAsesor: number,
    nombre: string
  ): AsesorSeleccionado {
    return {
      nmconsecutivo: nmconsecutivoAsesor,
      cdpersona: 0,
      cdtipoIdentificacion: '',
      nmidentificacion: '',
      dsnombre1: '',
      dsnombre2: '',
      dsapellido1: '',
      dsapellido2: '',
      dsnombreComercial: nombre,
      snactivo: 'S'
    };
  }

  private parseIsoDate(value: string | null): Date | null {
    if (!value) {
      return null;
    }

    const parts = value.split('-').map((part) => Number(part));
    if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) {
      return null;
    }

    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }

  private toNumber(value: string | null): number | null {
    if (!value) {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private validarFechas(): boolean {
    if (!this.fechaEntregaDesde() || !this.fechaEntregaHasta()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: 'Debe diligenciar fecha entrega desde y fecha entrega hasta'
      });
      return false;
    }

    if (this.fechaEntregaHasta()! < this.fechaEntregaDesde()!) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: 'La fecha final debe ser mayor o igual a la fecha inicial'
      });
      return false;
    }

    return true;
  }

  private toIsoDate(value: Date | null): string {
    const date = value ?? new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatearNombreAsesor(asesor: AsesorSeleccionado): string {
    const partes = [
      asesor.dsnombre1,
      asesor.dsnombre2,
      asesor.dsapellido1,
      asesor.dsapellido2
    ].filter((value) => value && value.trim().length > 0);

    if (partes.length > 0) {
      return partes.join(' ');
    }

    return asesor.dsnombreComercial || '';
  }
}
